import { Moment } from 'moment'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useAppDispatch } from '../../../App/Hooks/reduxHooks'
import { blockCategoryAdaptorSelector } from '../../../App/store/features/blockCategory/blockedCategorySlice'
import { Person } from '../../../App/store/features/person/IPerson'
import { Button } from '../../../Common/Button'
import { TextArea } from '../../../Common/TextInput'
import { store } from '../../../store'
import { getBlock } from '../../store/thunks/fetchBlock'
import { updateBlock } from '../../store/thunks/updateBlock'
import {
  IBlock,
  IBlockFormSubmit,
  IUpdateBlockData,
  IUpdateBlock,
} from '../../types'
import { isInstanceOfIThunkError } from '../../utils/interfaceCheck'
import { AppointmentDateRangePicker } from '../Inputs/AppointmentDateRangePicker'
import { CategorySelect } from '../Inputs/CategorySelect'
import { HourPicker } from '../Inputs/HourPicker'
import { UserPicker } from '../Inputs/UserPicker'

import { BlockedCategorySelectWrapper } from './style'

interface IProps {
  blockDetails: IBlock
  personList: Person[]
}

export const UpdateBlock = ({ blockDetails, personList }: IProps) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [blockNotes, setBlockNotes] = useState<string>(blockDetails.notes || '')

  const { setValue, handleSubmit } = useForm<IBlockFormSubmit>({
    mode: 'onSubmit',
    defaultValues: {
      blockedCategoryUuid: blockDetails.blockedCategoryUuid,
      personUuid: blockDetails.personUuid,
      notes: blockNotes,
      startsAt: blockDetails.startsAt,
      startHour: blockDetails.startsAt.clone().format('HH:mm'),
      endHour: blockDetails.startsAt.clone().add(60, 'm').format('HH:mm'),
    },
  })

  const [appointmentMoment] = useState<Moment>(blockDetails.startsAt)
  const blockCategories = blockCategoryAdaptorSelector.selectAll(
    store.getState(),
  )

  const onSubmit = (formObject: any) => {
    const startTimeSplit = formObject.startHour
      .split(':')
      .map((time: string) => parseInt(time, 10))

    const endTimeSplit = formObject.endHour
      .split(':')
      .map((time: string) => parseInt(time, 10))

    const updateFormData: IUpdateBlockData = {
      blockedCategoryUuid: formObject.blockedCategoryUuid,
      personUuid: formObject.personUuid,
      startsAt: formObject.startsAt
        .clone()
        .set('h', startTimeSplit[0])
        .set('m', startTimeSplit[1]),
      endsAt: formObject.startsAt
        .clone()
        .set('h', endTimeSplit[0])
        .set('m', endTimeSplit[1]),
      notes: blockNotes,
    }

    const updateBlockObject: IUpdateBlock = {
      data: updateFormData,
      blockUuid: blockDetails.uuid,
    }
    dispatch(updateBlock(updateBlockObject)).then(({ payload }) => {
      if (typeof payload !== 'undefined' && !isInstanceOfIThunkError(payload)) {
        dispatch(getBlock({ uuid: payload.blockUuid, type: 'create' }))
      }
      return null
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <BlockedCategorySelectWrapper>
        <CategorySelect
          setValue={setValue}
          blockedCategoryOptions={blockCategories}
        />
      </BlockedCategorySelectWrapper>
      <UserPicker persons={personList} setValue={setValue} />
      <AppointmentDateRangePicker
        setValue={setValue}
        appointmentMoment={appointmentMoment}
      />
      <HourPicker
        appointmentMoment={appointmentMoment}
        setValue={setValue}
        inputName="startHour"
      />
      <HourPicker
        appointmentMoment={appointmentMoment.clone().add(60, 'm')}
        setValue={setValue}
        inputName="endHour"
        labelName={t('End time')}
      />
      <label htmlFor="notes">
        <span>{t('Block note')}</span>
        <TextArea
          onChange={e => setBlockNotes(e.target.value)}
          width="400px"
          value={blockNotes}
        />
      </label>

      <Button type="submit">{t('Save')}</Button>
    </form>
  )
}
